//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import getter from 'dibs-object-getter';
import {
    TPN_TIER_MEMBER,
    TPN_TIER_BRONZE,
    TPN_TIER_SILVER,
    TPN_TIER_GOLD,
    TPN_TIER_PLATINUM,
} from 'dibs-trade-rewards-progress/exports/tpnStatusCodes';

import { BenefitsChartRow } from './BenefitsChartRow';
import { BenefitsChartCell } from './BenefitsChartCell';

import styles from './BenefitsChartHeader.scss';

import type { BenefitsChartHeader_tradeFirm as TradeFirmType } from './__generated__/BenefitsChartHeader_tradeFirm.graphql';
import type { BenefitsChartHeader_viewer as ViewerType } from './__generated__/BenefitsChartHeader_viewer.graphql';

type Props = {
    tradeFirm: TradeFirmType,
    viewer: ViewerType,
};

export function BenefitsChartHeaderComponent(props: Props) {
    const { tradeFirm, viewer } = props;

    const firmTPNRole =
        getter(tradeFirm)
            .at('rewards')
            .at('currentProgram')
            .at('programInfo')
            .value('code') || '';
    const tradeTPNRoles = getter(viewer).value('tradeProgramsQuery') || [];

    return (
        <BenefitsChartRow>
            <BenefitsChartCell className={styles.chartTitleCell}>
                <div className={styles.titleWrapper}>
                    <FormattedMessage
                        id="abt.loyaltyModules.BenefitsChartHeader.chartTitle"
                        defaultMessage="Trade Rewards Tiers"
                    />
                </div>
            </BenefitsChartCell>
            {tradeTPNRoles.map((tpnRole, i) => {
                const code = getter(tpnRole).value('code');
                const displayName = getter(tpnRole).value('displayName');
                const iconUri = getter(tpnRole).value('iconUri');
                const isFirmsTier = code === firmTPNRole;
                const isHighestRole = !tradeTPNRoles[i + 1];

                let pointsThreshold;
                const lowerThreshold = getter(tpnRole).value('minPoints') || 0;
                const upperThreshold = getter(tpnRole).value('maxPoints') || 0;
                if (code === TPN_TIER_MEMBER) {
                    pointsThreshold = (
                        <FormattedMessage
                            id="abt.loylatyModules.BenefitsChartHeader.memberPointsThreshold"
                            defaultMessage="Under {tpnMinimumPoints} Points"
                            values={{
                                tpnMinimumPoints: <FormattedNumber value={upperThreshold} />,
                            }}
                        />
                    );
                } else if (isHighestRole) {
                    pointsThreshold = (
                        <FormattedMessage
                            id="abt.loylatyModules.BenefitsChartHeader.maximalTpnPointsThreshold"
                            defaultMessage="{lowerThreshold} + Points"
                            values={{
                                lowerThreshold: <FormattedNumber value={lowerThreshold} />,
                            }}
                        />
                    );
                } else {
                    pointsThreshold = (
                        <FormattedMessage
                            id="abt.loylatyModules.BenefitsChartHeader.nonMaximalTpnPointsThreshold"
                            defaultMessage="{lowerThreshold} - {upperThreshold} Points"
                            values={{
                                lowerThreshold: <FormattedNumber value={lowerThreshold} />,
                                upperThreshold: <FormattedNumber value={upperThreshold} />,
                            }}
                        />
                    );
                }

                const cellClass = classnames(styles.tierTitleCell, {
                    [styles.memberCell]: code === TPN_TIER_MEMBER,
                    [styles.memberBgColor]: isFirmsTier && code === TPN_TIER_MEMBER,
                    [styles.bronzeBgColor]: isFirmsTier && code === TPN_TIER_BRONZE,
                    [styles.silverBgColor]: isFirmsTier && code === TPN_TIER_SILVER,
                    [styles.goldBgColor]: isFirmsTier && code === TPN_TIER_GOLD,
                    [styles.platinumBgColor]: isFirmsTier && code === TPN_TIER_PLATINUM,
                });

                const tierNameClass = classnames(styles.tierName, {
                    [styles.whiteTextColor]: isFirmsTier,
                    [styles.memberTextColor]: !isFirmsTier && code === TPN_TIER_MEMBER,
                    [styles.bronzeTextColor]: !isFirmsTier && code === TPN_TIER_BRONZE,
                    [styles.silverTextColor]: !isFirmsTier && code === TPN_TIER_SILVER,
                    [styles.goldTextColor]: !isFirmsTier && code === TPN_TIER_GOLD,
                    [styles.platinumTextColor]: !isFirmsTier && code === TPN_TIER_PLATINUM,
                });

                const pointsThresholdClass = classnames(styles.pointsThreshold, {
                    [styles.ironTextColor]: !isFirmsTier,
                    [styles.whiteTextColor]: isFirmsTier,
                });

                const currentTierArrowClass = classnames(styles.currentTierArrow, {
                    [styles.memberArrow]: code === TPN_TIER_MEMBER,
                    [styles.bronzeArrow]: code === TPN_TIER_BRONZE,
                    [styles.silverArrow]: code === TPN_TIER_SILVER,
                    [styles.goldArrow]: code === TPN_TIER_GOLD,
                    [styles.platinumArrow]: code === TPN_TIER_PLATINUM,
                });

                return (
                    <BenefitsChartCell className={cellClass} key={`chart-header-tier-${code}`}>
                        {isFirmsTier && (
                            <div className={styles.currentTierTab}>
                                <FormattedMessage
                                    id="abt.loylatyModules.BenefitsChartHeader.currentStatus"
                                    defaultMessage="Current Status"
                                />
                            </div>
                        )}
                        {code === TPN_TIER_BRONZE && (
                            <div className={styles.newTierIndicator}>
                                <FormattedMessage
                                    id="abt.loylatyModules.BenefitsChartHeader.newTierIndicator"
                                    defaultMessage="new"
                                />
                            </div>
                        )}
                        {iconUri && (
                            <div className={styles.tierDiamondSection}>
                                <div className={styles.diamondWrapper}>
                                    <img className={styles.diamondImage} src={iconUri} />
                                </div>
                            </div>
                        )}
                        <div>
                            <div className={tierNameClass}>{displayName}</div>
                            <div className={pointsThresholdClass}>{pointsThreshold}</div>
                        </div>
                        {isFirmsTier && (
                            <div className={styles.currentTierArrowContainer}>
                                <div className={currentTierArrowClass} />
                            </div>
                        )}
                    </BenefitsChartCell>
                );
            })}
        </BenefitsChartRow>
    );
}

export const BenefitsChartHeader = createFragmentContainer(BenefitsChartHeaderComponent, {
    tradeFirm: graphql`
        fragment BenefitsChartHeader_tradeFirm on TradeFirmType {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                }
            }
        }
    `,
    viewer: graphql`
        fragment BenefitsChartHeader_viewer on Viewer {
            tradeProgramsQuery(programCodes: ["MEMBER", "BRONZE", "SILVER", "GOLD", "PLATINUM"]) {
                code
                displayName
                iconUri
                maxPoints
                minPoints
            }
        }
    `,
});
